import {
    NAME_PATTERN,
    EMAIL_PATTERN,
    MESSAGE_MIN_LENGTH,
    AZIENDA_PATTERN,
    TELEFONO_PATTERN,
    CITTA_PATTERN,
    setMessage,
    setFieldState,
} from "./contatti";

// Modulo di richiesta della Pagina Contatti
// (template-parts/contatti-pagina/form.php): stesso endpoint REST e stessi
// helper di validazione condivisi di assets/js/contatti.js, ma un proprio set
// di campi (niente cognome/oggetto, azienda/telefono/città facoltativi,
// "motivo" letto da un gruppo di pillole radio invece che dalla listbox
// custom) — stesso principio di assets/js/servizio-richiesta.js per il
// modulo del singolo servizio: un modulo dedicato invece di allargare
// initContattiForm() a un quarto set di campi diverso.
function initContattiPaginaForm(form) {
    const submitButton = form.querySelector(".contatti-pagina-form__submit");
    const messageEl = form.querySelector("[data-contatti-message]");
    const nomeInput = form.querySelector('[name="nome"]');
    const aziendaInput = form.querySelector('[name="azienda"]');
    const emailInput = form.querySelector('[name="email"]');
    const telefonoInput = form.querySelector('[name="telefono"]');
    const cittaInput = form.querySelector('[name="citta"]');
    const messaggioInput = form.querySelector('[name="messaggio"]');
    const motivoInputs = Array.from(form.querySelectorAll('input[name="motivo"]'));

    const touched = new Set();

    function isMotivoValid() {
        return !motivoInputs.length || motivoInputs.some((input) => input.checked);
    }

    function validate() {
        const nomeValid = NAME_PATTERN.test(nomeInput.value.trim());
        // Azienda/telefono/città sono facoltativi qui (a differenza del
        // modulo del singolo servizio, dove azienda è obbligatoria): il
        // pattern si applica solo se il visitatore ha compilato il campo.
        const aziendaValid = !aziendaInput || !aziendaInput.value.trim() || AZIENDA_PATTERN.test(aziendaInput.value.trim());
        const emailValid = EMAIL_PATTERN.test(emailInput.value.trim());
        const telefonoValid = !telefonoInput || !telefonoInput.value.trim() || TELEFONO_PATTERN.test(telefonoInput.value.trim());
        const cittaValid = !cittaInput || !cittaInput.value.trim() || CITTA_PATTERN.test(cittaInput.value.trim());
        const messaggioValid = messaggioInput.value.trim().length >= MESSAGE_MIN_LENGTH;
        const motivoValid = isMotivoValid();

        setFieldState(form, "nome", nomeInput, nomeValid, touched.has("nome"));
        setFieldState(form, "azienda", aziendaInput, aziendaValid, touched.has("azienda"));
        setFieldState(form, "email", emailInput, emailValid, touched.has("email"));
        setFieldState(form, "telefono", telefonoInput, telefonoValid, touched.has("telefono"));
        setFieldState(form, "citta", cittaInput, cittaValid, touched.has("citta"));
        setFieldState(form, "messaggio", messaggioInput, messaggioValid, touched.has("messaggio"));
        setFieldState(form, "motivo", motivoInputs[0] || null, motivoValid, touched.has("motivo"));

        return nomeValid && aziendaValid && emailValid && telefonoValid && cittaValid && messaggioValid && motivoValid;
    }

    // Il submit resta sempre focalizzabile/abilitato, stesso principio di
    // initContattiForm() in assets/js/contatti.js: l'invalidità si segnala al
    // submit, non prima, spostando il focus sul primo campo errato.
    function focusFirstInvalid() {
        const firstInvalid = form.querySelector(".is-invalid");

        if (firstInvalid) {
            firstInvalid.focus();
        }
    }

    [
        [nomeInput, "nome"],
        [aziendaInput, "azienda"],
        [emailInput, "email"],
        [telefonoInput, "telefono"],
        [cittaInput, "citta"],
        [messaggioInput, "messaggio"],
    ].forEach(([input, name]) => {
        if (!input) {
            return;
        }

        input.addEventListener("input", () => {
            touched.add(name);
            validate();
        });
        input.addEventListener("blur", () => {
            touched.add(name);
            validate();
        });
    });

    motivoInputs.forEach((input) => {
        input.addEventListener("change", () => {
            touched.add("motivo");
            validate();
        });
    });

    validate();

    form.addEventListener("submit", async (event) => {
        event.preventDefault();

        touched.add("nome");
        touched.add("azienda");
        touched.add("email");
        touched.add("telefono");
        touched.add("citta");
        touched.add("messaggio");
        touched.add("motivo");

        if (!validate()) {
            setMessage(messageEl, "Controlla i campi evidenziati e correggi gli errori indicati.", "error");
            focusFirstInvalid();
            return;
        }

        const settings = (window.Castro && window.Castro.settings) || {};
        const formData = new FormData(form);
        const motivoChecked = motivoInputs.find((input) => input.checked);

        const payload = {
            nome: formData.get("nome") || "",
            email: formData.get("email") || "",
            azienda: formData.get("azienda") || "",
            telefono: formData.get("telefono") || "",
            citta: formData.get("citta") || "",
            messaggio: formData.get("messaggio") || "",
            motivo: motivoChecked ? motivoChecked.value : "",
            sito_web: formData.get("sito_web") || "",
            sorgente: formData.get("sorgente") || "",
            pagina_id: formData.get("pagina_id") || "",
            nonce: settings.contattiNonce || "",
        };

        submitButton.disabled = true;
        submitButton.classList.add("is-loading");
        setMessage(messageEl, "", null);

        try {
            const response = await fetch(`${settings.api_url}castro/v1/contatti`, {
                method: "POST",
                credentials: "same-origin",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setMessage(messageEl, data.message, "success");
                form.reset();
                touched.clear();
                validate();
            } else {
                setMessage(messageEl, data.message || "Si è verificato un errore, riprova più tardi.", "error");
                validate();
            }
        } catch (error) {
            setMessage(messageEl, "Impossibile inviare la richiesta, controlla la connessione e riprova.", "error");
            validate();
        } finally {
            // Il disabled qui serve solo a impedire un doppio invio mentre la
            // richiesta è in corso: va sempre rimosso a fine richiesta, non
            // lasciato al risultato della validazione (vedi validate()).
            submitButton.disabled = false;
            submitButton.classList.remove("is-loading");
        }
    });
}

// Accordion delle domande frequenti
// (template-parts/contatti-pagina/faq.php): un solo pannello aperto alla
// volta, toggle via [hidden] + aria-expanded. Il markup arriva dal server già
// visibile/espanso (senza JS le risposte restano sempre leggibili, vedi il
// commento in cima al template): è l'init qui sotto che le richiude per
// ottenere l'accordion, non un valore di default nel PHP.
function initContattiPaginaFaqItem(item) {
    const toggle = item.querySelector("[data-faq-toggle]");
    const panel = item.querySelector("[data-faq-panel]");

    if (!toggle || !panel) {
        return;
    }

    toggle.setAttribute("aria-expanded", "false");
    panel.hidden = true;

    toggle.addEventListener("click", () => {
        const isOpen = toggle.getAttribute("aria-expanded") === "true";
        const list = item.closest("[data-faq-list]");

        if (list) {
            list.querySelectorAll("[data-faq-item]").forEach((other) => {
                if (other === item) {
                    return;
                }

                const otherToggle = other.querySelector("[data-faq-toggle]");
                const otherPanel = other.querySelector("[data-faq-panel]");

                if (otherToggle) {
                    otherToggle.setAttribute("aria-expanded", "false");
                }
                if (otherPanel) {
                    otherPanel.hidden = true;
                }
                other.classList.remove("is-open");
            });
        }

        toggle.setAttribute("aria-expanded", isOpen ? "false" : "true");
        panel.hidden = isOpen;
        item.classList.toggle("is-open", !isOpen);
    });
}

export function initContattiPagina() {
    document.querySelectorAll("[data-contatti-pagina-form]").forEach(initContattiPaginaForm);
    document.querySelectorAll("[data-faq-item]").forEach(initContattiPaginaFaqItem);
}
